import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useCartStore from '../../context/cartStore';
import { useAuth } from '../../context/AuthContext';
import { ordersAPI, paymentAPI, couponsAPI, settingsAPI } from '../../utils/api';
import toast from 'react-hot-toast';

// Governorate IDs map to translation keys; the value sent to backend stays in English
const GOVERNORATES = [
  { id: 'alAsimah',         value: 'Al Asimah' },
  { id: 'hawalli',          value: 'Hawalli' },
  { id: 'farwaniya',        value: 'Farwaniya' },
  { id: 'ahmadi',           value: 'Ahmadi' },
  { id: 'jahra',            value: 'Jahra' },
  { id: 'mubarakAlKabeer',  value: 'Mubarak Al-Kabeer' },
];

const PAYMENT_METHOD_IDS = [
  { id: 'knet'       },
  { id: 'visa'       },
  { id: 'mastercard' },
  { id: 'applepay'   },
];

/* ─── Branded payment-method icons (inline SVG, no external assets) ─── */
const PaymentIcon = ({ name }) => {
  switch (name) {
    case 'knet':
      // KNET — official logo (blue background, yellow K), from uploaded SVG asset
      return (
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-label="KNET">
          <path d="M22.0785 4.32031H1.60134C0.893847 4.32031 0.320312 4.89385 0.320312 5.60134V18.3988C0.320312 19.1063 0.893847 19.6798 1.60134 19.6798H22.0785C22.786 19.6798 23.3595 19.1063 23.3595 18.3988V5.60134C23.3595 4.89385 22.786 4.32031 22.0785 4.32031Z" fill="#0873B9"/>
          <path d="M0.339844 8.40883C8.01916 8.40669 15.6989 8.40669 23.3791 8.40883V15.5371C15.6998 15.5392 8.02001 15.5392 0.339844 15.5371V8.40883ZM4.57171 8.968C4.56146 10.9376 4.57811 12.9078 4.57171 14.878C4.71567 14.9002 4.86097 14.9126 5.00662 14.9152C6.2159 14.9152 7.42583 14.9152 8.63448 14.9152C8.80229 14.9152 8.96819 14.8915 9.13472 14.8774C9.13856 14.1728 9.13472 13.4682 9.13792 12.7637C9.17169 12.4099 9.18388 12.0544 9.17443 11.6991C9.46074 11.9361 9.69837 12.2212 9.96226 12.4799C10.6028 13.1268 11.2343 13.7872 11.8838 14.4309C12.0046 14.5732 12.1403 14.702 12.2886 14.8152C12.3765 14.8233 12.4649 14.8263 12.5531 14.8242C14.6483 14.8152 16.7434 14.8338 18.8385 14.8152C18.7797 14.7308 18.7104 14.6541 18.6322 14.5872C18.0173 14.0825 17.4191 13.5579 16.81 13.05C16.2931 12.6381 15.8005 12.1994 15.2945 11.7754C15.2478 11.7158 15.1325 11.6556 15.1869 11.5698C15.4149 11.3949 15.6628 11.2437 15.8838 11.0573C16.4263 10.609 17.0213 10.2247 17.5671 9.78144C17.9082 9.54847 18.2358 9.29635 18.5483 9.02628C16.6076 9.00387 14.6662 9.02628 12.7254 9.01603C12.6241 9.01086 12.5226 9.01818 12.4231 9.03781C11.5591 9.67192 10.7315 10.3528 9.87195 10.9933C9.66569 11.1731 9.44359 11.3339 9.20838 11.4737C9.17379 10.641 9.20838 9.8013 9.19044 8.96608C7.64809 8.96928 6.10958 8.96351 4.57171 8.96608V8.968Z" fill="#0873B9"/>
          <path d="M4.57155 8.96484C6.10878 8.96484 7.64601 8.96805 9.18324 8.96484C9.20245 9.79751 9.16659 10.6372 9.20117 11.4725C9.43638 11.3326 9.65848 11.1719 9.86475 10.9921C10.7243 10.3516 11.5519 9.67069 12.4159 9.03659C12.5154 9.01694 12.6169 9.00962 12.7182 9.01481C14.659 9.02313 16.601 9.00264 18.5411 9.02505C18.2299 9.29344 17.9038 9.54407 17.5643 9.77574C17.0186 10.2203 16.4236 10.6026 15.8811 11.0516C15.6601 11.2367 15.4122 11.3879 15.1842 11.564C15.1298 11.6499 15.2483 11.7101 15.2918 11.7697C15.7978 12.193 16.2904 12.6324 16.8073 13.0443C17.4164 13.5567 18.0146 14.0793 18.6295 14.5815C18.7077 14.6484 18.777 14.725 18.8358 14.8095C16.7406 14.8281 14.6455 14.8095 12.5504 14.8185C12.4621 14.8206 12.3738 14.8176 12.2859 14.8095C12.1376 14.6963 12.0018 14.5675 11.8811 14.4252C11.2348 13.7847 10.6058 13.1211 9.95954 12.4742C9.69565 12.218 9.45802 11.9304 9.17171 11.6934C9.18116 12.0487 9.16897 12.4042 9.1352 12.758C9.1288 13.4625 9.1352 14.1671 9.132 14.8716C8.96547 14.8857 8.79957 14.9088 8.63176 14.9094C7.42311 14.9094 6.21318 14.9094 5.00389 14.9094C4.85825 14.9069 4.71295 14.8945 4.56899 14.8723C4.57796 12.9046 4.56194 10.9344 4.57155 8.96484Z" fill="#FAE80C"/>
        </svg>
      );

    case 'visa':
      // VISA — clean wordmark in classic Visa Blue with subtle yellow accent
      return (
        <svg viewBox="0 0 60 38" xmlns="http://www.w3.org/2000/svg" aria-label="Visa">
          <rect width="60" height="38" rx="4" fill="#FFFFFF" stroke="#E5E7EB" strokeWidth="1" />
          <g fill="#1A1F71" fontFamily="Arial Black, Arial, sans-serif" fontWeight="900" fontStyle="italic">
            <text x="30" y="25" textAnchor="middle" fontSize="14" letterSpacing="0.5">VISA</text>
          </g>
          {/* small accent dot/flag */}
          <rect x="38.5" y="13" width="3" height="2" fill="#F7B600" />
        </svg>
      );

    case 'mastercard':
      // Mastercard — interlocking red + yellow circles with orange overlap
      return (
        <svg viewBox="0 0 60 38" xmlns="http://www.w3.org/2000/svg" aria-label="Mastercard">
          <rect width="60" height="38" rx="4" fill="#FFFFFF" stroke="#E5E7EB" strokeWidth="1" />
          <circle cx="25" cy="19" r="9" fill="#EB001B" />
          <circle cx="35" cy="19" r="9" fill="#F79E1B" />
          {/* Overlap region — drawn as a path so we get the lens shape */}
          <path
            d="M30 12 a9 9 0 0 1 0 14 a9 9 0 0 1 0 -14 z"
            fill="#FF5F00"
          />
        </svg>
      );

    case 'applepay':
      // Apple Pay — Apple logo + "Pay" wordmark, black on white
      return (
        <svg viewBox="0 0 60 38" xmlns="http://www.w3.org/2000/svg" aria-label="Apple Pay">
          <rect width="60" height="38" rx="4" fill="#FFFFFF" stroke="#E5E7EB" strokeWidth="1" />
          {/* Apple silhouette (simplified) */}
          <g fill="#000000" transform="translate(11, 10) scale(0.55)">
            <path d="M19.665 16.811a4.84 4.84 0 0 1 2.302-4.066 4.96 4.96 0 0 0-3.91-2.117c-1.654-.174-3.234.99-4.073.99-.853 0-2.144-.971-3.527-.943a5.21 5.21 0 0 0-4.382 2.671c-1.886 3.27-.481 8.087 1.358 10.732.917 1.295 1.99 2.747 3.397 2.692 1.36-.054 1.875-.881 3.519-.881 1.633 0 2.117.881 3.55.851 1.474-.024 2.398-1.318 3.282-2.625.654-.93 1.166-1.954 1.51-3.05a4.674 4.674 0 0 1-2.852-4.305zM16.93 8.95c.752-.91 1.122-2.08 1.033-3.262a4.672 4.672 0 0 0-3.024 1.564c-.73.873-1.107 2-1.005 3.16a3.866 3.866 0 0 0 2.996-1.462z" />
          </g>
          {/* "Pay" text */}
          <g fill="#000000" fontFamily="-apple-system, Helvetica, Arial, sans-serif" fontWeight="600">
            <text x="29" y="25" fontSize="13"> Pay</text>
          </g>
        </svg>
      );

    default:
      return null;
  }
};

const CheckoutPage = () => {
  const { items, clearCart } = useCartStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const currency = isAr ? 'د.ك' : 'KD';
  const formatPrice = (n) => isAr ? `${n.toFixed(3)} ${currency}` : `${currency} ${n.toFixed(3)}`;

  const [form, setForm] = useState({
    full_name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '',
    phone: '', email: user?.email || '',
    address1: '', address2: '', area: '', governorate: 'Hawalli', country: 'Kuwait',
  });
  // Two payment methods: KNET (redirects to SadadPay) or Cash on Delivery.
  const [paymentMethod, setPaymentMethod] = useState('knet');
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [couponApplied, setCouponApplied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Shipping config — comes from /settings/public so admins can change it from Settings
  const [shippingFee, setShippingFee] = useState(1.5);
  const [freeThreshold, setFreeThreshold] = useState(10);

  useEffect(() => {
    settingsAPI.getPublic()
      .then(rows => {
        const map = Object.fromEntries((rows || []).map(r => [r.setting_key, r.setting_value]));
        const fee = parseFloat(map.standard_shipping_cost ?? map.shipping_fee);
        const thr = parseFloat(map.free_shipping_threshold);
        if (!isNaN(fee)) setShippingFee(fee);
        if (!isNaN(thr)) setFreeThreshold(thr);
      })
      .catch(() => { /* fall back to defaults */ });
  }, []);

  if (!items.length) {
    navigate('/shop');
    return null;
  }

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const shippingCost = subtotal >= freeThreshold ? 0 : shippingFee;
  const total = subtotal - discount + shippingCost;

  const handleInput = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  const validateForm = () => {
    const errs = {};
    if (!form.full_name)  errs.full_name  = t('checkout.validation.nameRequired');
    if (!form.phone)      errs.phone      = t('checkout.validation.phoneRequired');
    if (!form.email)      errs.email      = t('checkout.validation.emailRequired');
    if (!form.address1)   errs.address1   = t('checkout.validation.addressRequired');
    if (!form.governorate) errs.governorate = t('checkout.validation.governorateRequired');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const applyCoupon = async () => {
    if (!couponCode) return;
    try {
      const data = await couponsAPI.validate({ code: couponCode, subtotal });
      const d = Number(data.discount) || 0;
      setDiscount(d);
      setCouponApplied(true);
      toast.success(t('checkout.couponApplied', { amount: d.toFixed(3) }));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handlePlaceOrder = async () => {
    if (!validateForm()) return;
    setLoading(true);

    try {
      const orderData = await ordersAPI.create({
        items: items.map(i => ({ product_id: i.product_id, variant_id: i.variant_id, quantity: i.quantity })),
        payment_method: paymentMethod,    // 'cod' — cash on delivery
        coupon_code: couponApplied ? couponCode : null,
        guest_email: !user ? form.email : null,
        guest_phone: !user ? form.phone : null,
        notes: form.notes,
        shipping_address: {
          full_name: form.full_name, phone: form.phone,
          address1: form.address1, address2: form.address2,
          area: form.area, governorate: form.governorate, country: form.country,
        },
      });

      // Cash on Delivery — skip the gateway, go straight to success
      if (paymentMethod === 'cod') {
        clearCart();
        toast.success(t('checkout.orderPlaced', { order: orderData.order_number }), {
          icon: '🍯', duration: 3000,
        });
        navigate(`/checkout/success?order=${orderData.order_number}`);
        return;
      }

      // KNET / SadadPay flow: get hosted payment URL and redirect
      const paymentData = await paymentAPI.initiate({
        order_number: orderData.order_number,
        payment_method: paymentMethod,
      });

      clearCart();

      if (!paymentData.payment_url) {
        // No URL returned — fall back so the order isn't stranded
        navigate(`/checkout/success?order=${orderData.order_number}`);
        return;
      }

      toast.success(t('checkout.orderPlaced', { order: orderData.order_number }), {
        icon: '🍯', duration: 1500,
      });

      // Give the toast ~200ms to paint before we hand off to Sadad's hosted page
      setTimeout(() => { window.location.href = paymentData.payment_url; }, 200);
    } catch (err) {
      toast.error(err.message || t('checkout.orderFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="checkout-page" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="container">
        <h1 className="checkout-title">{t('checkout.title')}</h1>

        <div className="checkout-layout">
          {/* LEFT: Form */}
          <div className="checkout-form">
            {/* Contact */}
            <div className="checkout-section">
              <h3>{t('checkout.contactInfo')}</h3>
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">{t('checkout.fullName')} *</label>
                  <input name="full_name" value={form.full_name} onChange={handleInput} className={`form-control ${errors.full_name ? 'error' : ''}`} placeholder={t('checkout.fullNamePlaceholder')} />
                  {errors.full_name && <p className="error-msg">{errors.full_name}</p>}
                </div>
                <div className="form-group">
                  <label className="form-label">{t('checkout.phone')} *</label>
                  <input name="phone" value={form.phone} onChange={handleInput} className={`form-control ${errors.phone ? 'error' : ''}`} placeholder={t('checkout.phonePlaceholder')} />
                  {errors.phone && <p className="error-msg">{errors.phone}</p>}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('checkout.email')} *</label>
                <input name="email" type="email" value={form.email} onChange={handleInput} className={`form-control ${errors.email ? 'error' : ''}`} placeholder={t('checkout.emailPlaceholder')} />
                {errors.email && <p className="error-msg">{errors.email}</p>}
              </div>
            </div>

            {/* Shipping Address */}
            <div className="checkout-section">
              <h3>{t('checkout.deliveryAddress')}</h3>
              <div className="form-group">
                <label className="form-label">{t('checkout.address1')} *</label>
                <input name="address1" value={form.address1} onChange={handleInput} className={`form-control ${errors.address1 ? 'error' : ''}`} placeholder={t('checkout.address1Placeholder')} />
                {errors.address1 && <p className="error-msg">{errors.address1}</p>}
              </div>
              <div className="form-group">
                <label className="form-label">{t('checkout.address2')}</label>
                <input name="address2" value={form.address2} onChange={handleInput} className="form-control" placeholder={t('checkout.address2Placeholder')} />
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">{t('checkout.area')}</label>
                  <input name="area" value={form.area} onChange={handleInput} className="form-control" placeholder={t('checkout.areaPlaceholder')} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('checkout.governorate')} *</label>
                  <select name="governorate" value={form.governorate} onChange={handleInput} className={`form-control ${errors.governorate ? 'error' : ''}`}>
                    {GOVERNORATES.map(g => (
                      <option key={g.value} value={g.value}>{t(`checkout.governorates.${g.id}`)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('checkout.orderNotes')}</label>
                <textarea name="notes" value={form.notes || ''} onChange={handleInput} className="form-control" rows="2" placeholder={t('checkout.orderNotesPlaceholder')} />
              </div>
            </div>

            {/* Payment — KNET (SadadPay) or Cash on Delivery */}
            <div className="checkout-section">
              <h3>{t('checkout.paymentMethod')}</h3>
              <div className="payment-methods">
                {[
                  { id: 'knet', icon: <PaymentIcon name="knet" /> },
                  { id: 'cod',  icon: <span aria-hidden="true" style={{ fontSize: '1.6rem' }}>💵</span> },
                ].map(pm => (
                  <label key={pm.id} className={`payment-method ${paymentMethod === pm.id ? 'active' : ''}`}>
                    <input type="radio" name="payment" value={pm.id}
                      checked={paymentMethod === pm.id}
                      onChange={() => setPaymentMethod(pm.id)} />
                    <span className="pm-icon">{pm.icon}</span>
                    <div>
                      <strong>{t(`checkout.payment.${pm.id}`)}</strong>
                      <span>{t(`checkout.payment.${pm.id}Desc`)}</span>
                    </div>
                  </label>
                ))}
              </div>
              <p className="payment-note">
                {paymentMethod === 'cod'
                  ? `📦 ${t('checkout.codNote')}`
                  : `🔒 ${t('checkout.paymentSecuredSadad')}`}
              </p>
            </div>
          </div>

          {/* RIGHT: Order Summary */}
          <div className="checkout-summary">
            <div className="checkout-summary__box">
              <h3>{t('checkout.orderSummary')}</h3>
              <div className="checkout-items">
                {items.map(item => (
                  <div key={item.key} className="checkout-item">
                    <div className="checkout-item__img">
                      {item.image ? <img src={item.image} alt={item.name} /> : <span>🍯</span>}
                      <span className="checkout-item__qty">{item.quantity}</span>
                    </div>
                    <div className="checkout-item__info">
                      <p>{item.name}</p>
                      {item.variant_label && <small>{item.variant_label}</small>}
                    </div>
                    <p className="checkout-item__price">{formatPrice(item.price * item.quantity)}</p>
                  </div>
                ))}
              </div>

              {/* Coupon */}
              <div className="checkout-coupon">
                <input
                  type="text" placeholder={t('checkout.couponPlaceholder')} value={couponCode}
                  onChange={e => setCouponCode(e.target.value.toUpperCase())}
                  className="form-control" disabled={couponApplied}
                />
                <button className="btn btn-outline" onClick={applyCoupon} disabled={couponApplied}>
                  {couponApplied ? '✓' : t('checkout.apply')}
                </button>
              </div>

              <div className="checkout-totals">
                <div><span>{t('checkout.subtotal')}</span><span>{formatPrice(subtotal)}</span></div>
                {discount > 0 && <div className="discount-row"><span>{t('checkout.discount')}</span><span>-{formatPrice(discount)}</span></div>}
                <div><span>{t('checkout.shipping')}</span><span>{shippingCost === 0 ? `🎉 ${t('checkout.free')}` : formatPrice(shippingCost)}</span></div>
                <div className="total-row"><span>{t('checkout.total')}</span><span>{formatPrice(total)}</span></div>
              </div>

              <button className="btn btn-primary checkout-submit" onClick={handlePlaceOrder} disabled={loading}>
                {loading
                  ? t('checkout.processing')
                  : `${t('checkout.payNow', { amount: total.toFixed(3) })} ${isAr ? '←' : '→'}`}
              </button>
              <p className="checkout-terms">{t('checkout.termsNote')}</p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .checkout-page { padding: 50px 0 80px; }
        .checkout-title { font-family: var(--font-serif); margin-bottom: 36px; }
        .checkout-layout { display: grid; grid-template-columns: 1fr 400px; gap: 40px; align-items: start; }
        .checkout-section { background: var(--white); border-radius: var(--radius-lg); padding: 28px; margin-bottom: 20px; }
        .checkout-section h3 { font-family: var(--font-serif); font-size: 1.1rem; margin-bottom: 20px; color: var(--olive); }
        .form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .payment-methods { display: flex; flex-direction: column; gap: 10px; margin-bottom: 12px; }
        .payment-method {
          display: flex; align-items: center; gap: 14px; padding: 14px 16px;
          border: 2px solid var(--border); border-radius: var(--radius); cursor: pointer; transition: all 0.2s;
        }
        .payment-method.active { border-color: var(--gold); background: rgba(212,148,58,0.05); }
        .payment-method input { display: none; }
        /* Branded payment icon container — fixed size so all logos line up */
        .pm-icon {
          flex-shrink: 0;
          width: 48px;
          height: 30px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .pm-icon svg {
          width: 100%;
          height: 100%;
          display: block;
        }
        .payment-method strong { display: block; font-size: 0.9rem; }
        .payment-method span:not(.pm-icon) { font-size: 0.75rem; color: var(--text-muted); }
        .payment-note { font-size: 0.8rem; color: var(--text-muted); margin-top: 8px; }
        .checkout-summary__box {
          background: var(--white); border-radius: var(--radius-lg); padding: 28px;
          position: sticky; top: 90px;
        }
        .checkout-summary__box h3 { font-family: var(--font-serif); margin-bottom: 20px; }
        .checkout-items { display: flex; flex-direction: column; gap: 12px; margin-bottom: 20px; }
        .checkout-item { display: flex; align-items: center; gap: 12px; }
        .checkout-item__img { position: relative; width: 56px; height: 56px; border-radius: 8px; background: var(--cream); overflow: hidden; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
        .checkout-item__img img { width: 100%; height: 100%; object-fit: cover; }
        .checkout-item__qty {
          position: absolute; top: -6px; right: -6px; width: 18px; height: 18px;
          background: var(--gold); color: white; border-radius: 50%; font-size: 0.65rem;
          font-weight: 700; display: flex; align-items: center; justify-content: center;
        }
        [dir="rtl"] .checkout-item__qty { right: auto; left: -6px; }
        .checkout-item__info { flex: 1; }
        .checkout-item__info p { font-size: 0.88rem; font-weight: 500; }
        .checkout-item__info small { font-size: 0.75rem; color: var(--text-muted); }
        .checkout-item__price { font-weight: 700; font-size: 0.88rem; white-space: nowrap; }
        .checkout-coupon { display: flex; gap: 8px; margin-bottom: 20px; }
        .checkout-totals { border-top: 1px solid var(--border); padding-top: 16px; display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px; }
        .checkout-totals div { display: flex; justify-content: space-between; font-size: 0.9rem; }
        .checkout-totals .discount-row { color: var(--olive); }
        .checkout-totals .total-row { font-weight: 700; font-size: 1.1rem; padding-top: 10px; border-top: 2px solid var(--border); }
        .checkout-submit { width: 100%; padding: 16px; font-size: 1rem; }
        .checkout-terms { font-size: 0.72rem; color: var(--text-muted); text-align: center; margin-top: 12px; }
        @media (max-width: 900px) { .checkout-layout { grid-template-columns: 1fr; } .checkout-summary__box { position: static; } }
        @media (max-width: 480px) { .form-grid-2 { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
};

export default CheckoutPage;
